import { useCallback, useEffect, useState } from "react";
import type { IUserContext } from "@origin-digital/event-dispatcher";
import { fetchUserContextV3 } from "../UserContext/fetchUserContext";

/**
 * A React hook that automatically fetches the user's context and keeps it up to date by polling
 * at a regular interval. It also provides a `refresh` function that can be used to manually
 * trigger a refetch with cache busting enabled.
 *
 * - On initial mount, it fetches the user context without cache busting.
 * - It sets up a polling interval (default: 15 minutes) to refetch the user context periodically.
 * - The `refresh()` method forces a fetch with cache busting (e.g. appending a timestamp or flag to the request).
 * - Handles loading and error states.
 *
 * @param intervalMs - Optional polling interval in milliseconds (default: 15 minutes).
 * @returns An object containing:
 *   - `data`: The latest `IUserContext` value or `undefined` if not yet fetched.
 *   - `error`: Any error encountered during fetch, or `undefined` if successful.
 *   - `loading`: Whether a fetch is currently in progress.
 *   - `refresh`: A callback to manually trigger a cache-busting refetch.
 */
export const useAutoFetchUserContext = (intervalMs = 15 * 60 * 1000) => {
  const [data, setData] = useState<IUserContext | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const getData = useCallback(async (cacheBust: boolean) => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await fetchUserContextV3(cacheBust);
      setData(result);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    getData(true);
  }, [getData]);

  useEffect(() => {
    getData(false);
    const intervalId = setInterval(() => {
      getData(false);
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [getData, intervalMs]);

  return { data, error, loading, refresh };
};
